import { IDecayCalibration } from '@models/decay/decay-calibration';
import { IDecayClosedAccount } from '@models/decay/decay-closed-account';
import { IDecayEstimate, IDecayResult, IEstimateResult } from '@models/decay/decay-estimate';
import { IDecayFactor } from '@models/decay/decay-factors';
import { LinearGradient } from '@progress/kendo-drawing';
import { getDecayCalibrationTypeSettings } from '@services/decay/decay-calibration-strings';
import { formatMoney } from '@shared/currency-formatter';
import * as Highcharts from 'highcharts';
import { IExportChartOption } from '@components/export/export.config';

declare var require: any;
const Boost = require('highcharts/modules/boost');
const noData = require('highcharts/modules/no-data-to-display');
const More = require('highcharts/highcharts-more');
const exporting = require('highcharts/modules/exporting');

Boost(Highcharts);
noData(Highcharts);
More(Highcharts);
noData(Highcharts);
exporting(Highcharts);

export const AVERAGE_VINTAGE_BAL_CHART_ID = 'AVERAGE_VINTAGE_BALANCE_CHART_ID';

export const getDecayChartOptions = (calibrationType: number) => {
  const chartOptions: any = {
    chart: {
      type: 'column',
      zoomType: 'x',
      events: {
        selection(event) {
          let text;
          let label;
          if (event.xAxis) {
            text =
              'min: ' +
              Highcharts.dateFormat('%e %b %y', event.xAxis[0].min) +
              ', max: ' +
              Highcharts.dateFormat('%e %b %y', event.xAxis[0].max);
          } else {
            text = 'Selection reset';
          }
          label = this.renderer
            .label(text, 100, 120)
            .attr({
              fill: Highcharts.getOptions().colors[0],
              padding: 10,
              r: 5,
              zIndex: 8
            })
            .css({
              color: '#FFFFFF'
            })
            .add();

          setTimeout(() => {
            label.fadeOut();
          }, 1000);
        }
      }
    },

    title: {
      text: null
    },

    yAxis: [
      {
        gridLineWidth: 1,
        ...getDecayCalibrationTypeSettings(calibrationType).chartUnit
      }
    ],
    xAxis: {
      min: 1,
      allowDecimals: false
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      alignColumns: true,
      itemWidth: 160
    },
    plotOptions: {
      series: {},
      line: {
        marker: {
          enabled: false
        }
      }
    },
    tooltip: {
      enabled: true,
      formatter() {
        const value = formatMoney(this.y, 0);
        return `x: ${this.x}<br><span style="color:${this.point.color}">●</span> <b>${this.series.name}:</b> <span ${
          this.y < 0 ? 'style="color:red"' : ''
        } >${value}</span>`;
      }
    },
    //  / xAxis: { type: 'string' },
    series: [
      {
        id: AVERAGE_VINTAGE_BAL_CHART_ID,
        name: getDecayCalibrationTypeSettings(calibrationType).title,
        color: Highcharts.getOptions().colors[0],
        animation: {
          duration: 1000,
          easing: 'easeOutBounce'
        },
        yAxis: 0,
        type: 'column'
      }
    ],
    credits: {
      enabled: false
    },
    navigation: {
      buttonOptions: {
        enabled: true
      }
    },
    exporting: {
      allowHTML: true
    }
  };
  return chartOptions;
};

export const getAverageClosedAccountsChart = (
  closedAccounts: IDecayClosedAccount[],
  decayFactor: IDecayFactor[],
  exportChartOption?: IExportChartOption
) => {
  const closureRatesCategories = [];
  const closureRatesData = [];

  const impliedDecay = [];

  closedAccounts.forEach(x => {
    //@ts-ignore
    closureRatesCategories.push(x.PeriodEndDate.substr(0, 7));
    closureRatesData.push(x.ClosureRate);
  });
  decayFactor.forEach(x => {
    impliedDecay.push([x.Observation, isNaN(x.Factor) ? x.Factor : parseFloat(x.Factor.toFixed(4))]);
  });

  const options = {
    exporting: exportChartOption
      ? {
          sourceWidth: exportChartOption.width,
          sourceHeight: exportChartOption.height,
          allowHTML: true
        }
      : {},
    chart: {
      type: 'column',
      zoomType: 'x'
      // marginLeft: 800
    },

    title: {
      text: null
    },

    xAxis: [
      {
        // Closure Rates
        categories: closureRatesCategories,
        width: '40%',
        left: '0%',
        top: 50
      },
      {
        // Implied Decay
        width: '40%',
        left: '56%'
      }
    ],

    yAxis: [
      {
        // Closure Rates
        title: {
          text: 'Closure Rates'
        },
        min: 0,
        width: '40%',
        left: '0%',
        top: 50
      },
      {
        title: {
          text: 'Implied Decay'
        },
        // Implied Decay
        width: '40%',
        left: '56%',
        top: 50
      }
    ],

    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      alignColumns: true,
      itemWidth: 160
    },
    credits: {
      enabled: false
    },
    navigation: {
      buttonOptions: {
        enabled: true
      }
    },
    series: [
      {
        name: 'Closed Accounts',
        data: closureRatesData,
        xAxis: 0,
        yAxis: 0
      },
      {
        name: 'Implied Decay',
        type: 'line',
        xAxis: 1,
        yAxis: 1,
        data: impliedDecay
      }
    ]
  };
  return options;
};

/**
 * This method is used to clean up the chart after the user reloads estimates by saving
 * or by clearing the local storage. The system will re-add estimates with different ID's after
 * these actions this causes many lines to be placed on the chart. This method will remove any
 * series that isn't the average vintage balance.
 */
export const removeAllEstimatesFromChart = (chart: Highcharts.Chart): void => {
  const series = chart.series;
  const idsToRemove: string[] = [];

  series.forEach(element => {
    if (element.name.includes('Estimate')) {
      idsToRemove.push(element.options.id);
    }
  });

  idsToRemove.forEach(id => {
    chart.get(id).remove(false);
  });
};
export const padWithZeroes = (estimate: IDecayEstimate, dataMax: number): IDecayEstimate => {
  if (estimate.DecayResult && estimate.DecayResult.DecayEstimateResults && estimate.DecayResult.DecayEstimateResults.length < dataMax) {
    const numMissingValues = dataMax - estimate.DecayResult.DecayEstimateResults.length;

    for (let i = 0; i < numMissingValues; i++) {
      const padding: IEstimateResult = {
        Observation: i + estimate.DecayResult.DecayEstimateResults.length,
        Result: 0,
        DecayFactor: 0
      };
      estimate.DecayResult.DecayEstimateResults.push(padding);
    }
  }
  return estimate;
};

/**
 * Adds, removes or updates a given list of estimates on that chart
 */
export const updateEstimatesToChart = (chart: Highcharts.Chart, estimates: IDecayEstimate[], decayCalibrationType: number) => {
  chart.update(getDecayChartOptions(decayCalibrationType));

  // Getting the max pad value
  let maxValue = chart.series[0].data.length;
  estimates.forEach(est => {
    if (est.Id && est.Curtailment > maxValue) {
      maxValue = est.Curtailment;

      if (maxValue < 180) {
        maxValue++;
      }
    }
  });

  if (estimates && estimates.length > 0) {
    estimates.forEach((estimate: IDecayEstimate) => {
      // Deciding if we need to add or update
      const chartEst = chart.get(estimate.Id);
      if (chartEst) {
        estimate = padWithZeroes(estimate, maxValue);

        const options = {
          data: estimate.DecayResult.DecayEstimateResults.map((bal, index) => [index + 1, bal.Result]),
          name: estimate.IsChosen ? `${estimate.Name} *` : estimate.Name
        };
        // @ts-ignore
        chartEst.update(options, false);
      } else {
        if (estimate.Id) {
          estimate = padWithZeroes(estimate, maxValue);
          const lineChartOptions: any = {
            id: estimate.Id,
            type: 'line',
            dashStyle: 'Dash',
            color: estimate.Colour,
            shadow: { color: estimate.Colour },
            textColor: estimate.Colour,
            connectEnds: true,
            name: estimate.IsChosen ? `${estimate.Name} *` : estimate.Name,
            data: estimate.DecayResult.DecayEstimateResults.map((bal, index) => [index + 1, bal.Result]),
            animation: {
              duration: (0.5 + 0.5 * Math.random()) * 2000,
              easing: 'easeOutBounce'
            }
          };
          chart.addSeries(lineChartOptions, false);
        }
      }
    });

    chart.series.forEach(series => {
      const seriesId = series.options.id;
      // Removing estimates
      if (seriesId !== AVERAGE_VINTAGE_BAL_CHART_ID) {
        const index = estimates.findIndex(est => est.Id === seriesId);
        if (index === -1) {
          // This series does not exist anymore so we will remove  it
          chart.get(seriesId).remove(false);
        }
      }
    });
    // @ts-ignore

    chart.redraw();
  }
};

export const populateVintageObservationAverages = (chart: Highcharts.Chart, balanceResult: IDecayResult, redraw = false) => {
  const positiveObservations =
    balanceResult && balanceResult.ObservationAverages ? balanceResult.ObservationAverages.filter(o => o.Observation > 0) : [];

  if (chart && positiveObservations.length > 0) {
    const data = [];

    for (let i = 0; i < positiveObservations.length && i < balanceResult.MaxObservations; i++) {
      data.push([i + 1, positiveObservations[i].Value]);
    }

    const options = { data };

    // @ts-ignore
    chart.get(AVERAGE_VINTAGE_BAL_CHART_ID).update(options, redraw);
  }
};
